import { getWindow } from '@surfline/web-common';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { OverlayPhase } from 'components/LineupCamOverlay';
import { incrementVisits, incrementShown, shouldShowOverlay } from './overlayStorage';

const DEFAULT_PREVIEW_DURATION_SECONDS = 10;
const DEFAULT_OVERLAY_HOLD_MS = 2500;
const COOKIE_PREFIX = 'sl_lineup_cam_preview_seen_';
const COOKIE_EXPIRATION_DAYS = 1;

interface UseLineupCamPreviewProps {
  cameraId: string;
  isLineupCam: boolean;
  hasLineupCamPermissions: boolean;
  isPaywallEnabled: boolean;
  isPreviewEnabled: boolean;
  isStreamReady: boolean;
  previewDurationSeconds?: number;
  hasOverlayData?: boolean;
  isOverlayDataLoading?: boolean;
  overlayHoldMs?: number;
  skipPreviewCookie?: boolean;
}

interface LineupCamPreviewData {
  isPreviewActive: boolean;
  isCountdownRunning: boolean;
  previewSecondsRemaining: number;
  previewChecked: boolean;
  overlayPhase: OverlayPhase;
  onExpandComplete: () => void;
}

export const useLineupCamPreview = ({
  cameraId,
  isLineupCam,
  hasLineupCamPermissions,
  isPaywallEnabled,
  isPreviewEnabled,
  isStreamReady,
  previewDurationSeconds = DEFAULT_PREVIEW_DURATION_SECONDS,
  hasOverlayData = false,
  isOverlayDataLoading = false,
  overlayHoldMs = DEFAULT_OVERLAY_HOLD_MS,
  skipPreviewCookie = false,
}: UseLineupCamPreviewProps): LineupCamPreviewData => {
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const [isCountdownRunning, setIsCountdownRunning] = useState(false);
  const [previewChecked, setPreviewChecked] = useState(false);
  const [previewSecondsRemaining, setPreviewSecondsRemaining] = useState(previewDurationSeconds);
  const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>('idle');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);

  const previewCameraIdRef = useRef<string | null>(null);
  const previewShownForRef = useRef<Set<string>>(new Set());

  const [useOverlayFlow, setUseOverlayFlow] = useState(false);

  // Sync previewSecondsRemaining when prop changes before countdown starts
  useEffect(() => {
    if (!isCountdownRunning) {
      setPreviewSecondsRemaining(previewDurationSeconds);
    }
  }, [previewDurationSeconds, isCountdownRunning]);

  // Check conditions and activate preview on client (SSR-safe)
  useEffect(() => {
    if (!getWindow()) return;

    // If user switched away from the cam that had an active preview, end it
    if (previewCameraIdRef.current && previewCameraIdRef.current !== cameraId) {
      previewCameraIdRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      setIsPreviewActive(false);
      setIsCountdownRunning(false);
      setPreviewSecondsRemaining(previewDurationSeconds);
      setOverlayPhase('idle');
      setUseOverlayFlow(false);
    }

    // Wait for overlay data before deciding — the flow locks in on first run.
    if (isOverlayDataLoading) return;

    const cookieName = `${COOKIE_PREFIX}${cameraId}`;
    const hasSeenPreview = skipPreviewCookie ? false : !!Cookies.get(cookieName);

    const shouldShowPreview =
      isLineupCam &&
      !hasLineupCamPermissions &&
      isPaywallEnabled &&
      isPreviewEnabled &&
      !hasSeenPreview &&
      !previewShownForRef.current.has(cameraId);

    if (shouldShowPreview) {
      if (!skipPreviewCookie) {
        const expire = new Date();
        expire.setDate(expire.getDate() + COOKIE_EXPIRATION_DAYS);
        Cookies.set(cookieName, 'true', { expires: expire, path: '/' });
      }
      previewShownForRef.current.add(cameraId);
      setIsPreviewActive(true);
      previewCameraIdRef.current = cameraId;

      // Always increment visit counter when preview plays (for overlay frequency gating)
      const stats = incrementVisits(cameraId);
      if (hasOverlayData) {
        setUseOverlayFlow(shouldShowOverlay(stats.visits, stats.shown));
      } else {
        setUseOverlayFlow(false);
      }
    }

    setPreviewChecked(true);
  }, [
    cameraId,
    isLineupCam,
    hasLineupCamPermissions,
    isPaywallEnabled,
    isPreviewEnabled,
    previewDurationSeconds,
    skipPreviewCookie,
    hasOverlayData,
    isOverlayDataLoading,
  ]);

  // Overlay phase: idle → overlay when preview active + overlay conditions met + stream ready
  // Wait for stream to be ready so the user sees the video in the cutout box immediately (no black box)
  useEffect(() => {
    if (isPreviewActive && useOverlayFlow && overlayPhase === 'idle' && isStreamReady) {
      setOverlayPhase('overlay');
    }
  }, [isPreviewActive, useOverlayFlow, overlayPhase, isStreamReady]);

  // Overlay phase: overlay → expanding after hold duration
  useEffect(() => {
    if (overlayPhase !== 'overlay') return;

    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      setOverlayPhase('expanding');
    }, overlayHoldMs);

    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    };
  }, [overlayPhase, overlayHoldMs]);

  const onExpandComplete = useCallback(() => {
    if (overlayPhase === 'expanding') {
      setOverlayPhase('preview');
    }
  }, [overlayPhase]);

  // Overlay frequency: record that overlay was shown when it first displays
  useEffect(() => {
    if (overlayPhase === 'overlay') {
      incrementShown(cameraId);
    }
  }, [overlayPhase, cameraId]);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) return;
    setIsCountdownRunning(true);
    intervalRef.current = setInterval(() => {
      setPreviewSecondsRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPreviewActive(false);
          setOverlayPhase('done');
          return 0;
        }
        return next;
      });
    }, 1000);
  }, []);

  // Start countdown when stream is ready (non-overlay flow) or when overlay expand completes
  useEffect(() => {
    if (!isPreviewActive || !isStreamReady) return;

    // For overlay flow, wait until expand animation completes (preview phase)
    if (useOverlayFlow && overlayPhase !== 'preview') return;

    startCountdown();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isPreviewActive, isStreamReady, startCountdown, useOverlayFlow, overlayPhase]);

  return {
    isPreviewActive,
    isCountdownRunning,
    previewSecondsRemaining,
    previewChecked,
    overlayPhase,
    onExpandComplete,
  };
};
